import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../components/buttons/IconButton';
import { SHARED_PROJECT_TYPE_TABS } from './constants';
import { useCallback, useEffect } from 'react';
import { formatNumber, getTooltipId } from '../../../utils/utils';
import { Tooltip } from 'react-tooltip';
import ChipsList from '../../../components/common/ChipsList';
import RegisterStatusBadge from '../../../components/common/RegisterStatusBadge';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { openInNewTabOnModifier } from '../../../utils/openInNewTab';

const ProfilerFeedbackSearchResultsTabEntry = observer(
  ({
    entry = {},
    feedbacks = {},
    project = {},
    onChangeFeedback,
    params,
    displayDetails = () => {},
    index
  }) => {
    const { utilsStore, profilerProjectStore, profilerAccessStore } = useStore();

    const state = useLocalObservable(() => ({
      entryFeedback: [],
      nameTooltipId: getTooltipId(),
      grossProfitTooltipId: getTooltipId(),
      basicRevenueTooltipId: getTooltipId(),
      basicEmployeesTooltipId: getTooltipId(),
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      }
    }));

    useEffect(() => {
      return () => {
        if (state.isDetailsPopupOpen) {
          state.toggleDetailsPopup();
        }
      };
    }, [state, entry.id]);

    useEffect(() => {
      state.feedbacks = project.feedbacks.find((project) => project.resultId === entry.id);
    }, [state, entry, project]);

    const isFeedbackMarked = useCallback(
      (resultId, feedback) => {
        return feedbacks.find((fb) => fb.resultId === resultId && fb.feedback === feedback);
      },
      [feedbacks]
    );

    const readOnly = params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED;

    const openEntryInNewTab = () =>
      utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });

    // Cmd/Ctrl-click opens a new tab; a plain click opens details in place. The
    // right-click "Open in new tab" menu shares the same openEntryInNewTab thunk.
    const onOpenEntryDetails = openInNewTabOnModifier(openEntryInNewTab, () =>
      displayDetails(entry)
    );

    const isInWatchlist = profilerProjectStore.watchlist.findIndex((c) => c.id === entry.id) !== -1;

    const watchlistButtonTooltipContent = !profilerAccessStore.canViewProfilerWatchlist ? (
      <>
        Requires Profiler Lite or Profiler Full access.
        <br />
        Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
        assistance.
      </>
    ) : isInWatchlist ? (
      <>Remove from watchlist</>
    ) : (
      <>Add to watchlist</>
    );
    const watchlistButtonTooltipPosition = !profilerAccessStore.canViewProfilerWatchlist
      ? 'bottom-end'
      : state.layout === 'desktop'
        ? 'bottom-center'
        : 'bottom-end';
    const watchlistButtonTooltipClickable = !profilerAccessStore.canViewProfilerWatchlist;

    const onWatchlistButtonClick = () => {
      profilerProjectStore.addToWatchlist(entry);
    };

    return (
      <div
        className="row result-row-entry profiler-search-cols"
        style={index === 0 ? { marginTop: '8px' } : {}}
      >
        {entry.showScoring && (
          <>
            <div
              className="result-scoring-badge noselect"
              id={`result-scoring-badge-${entry.id}`}
              style={{ backgroundColor: entry.scoreSecondaryColor }}
            >
              <div className="score-icon-wrap">
                <img
                  src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                  style={{ filter: entry.scoreIconFilter }}
                />
              </div>
              <div className="score-label" style={{ color: entry.scorePrimaryColor }}>
                {entry.scoreLabel}
              </div>
            </div>
            <Tooltip
              anchorSelect={`#result-scoring-badge-${entry.id}`}
              multiline={true}
              variant="light"
              place="bottom-end"
              opacity={1}
              content={entry.scoreTooltipText}
              className={entry.scoreTooltipClassName}
              clickable
            />
          </>
        )}
        {state.layout === 'mobile' && (
          <div className="item feedback">
            <IconButton
              type={ICON_BUTTON_TYPES.GREEN}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'up')}
              filled={isFeedbackMarked(entry.id, 'up')}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.YELLOW}
              icon={ICON_BUTTON_ICONS.QUESTION_LG}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'question')}
              filled={isFeedbackMarked(entry.id, 'question')}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'down')}
              filled={isFeedbackMarked(entry.id, 'down')}
            />
          </div>
        )}
        <div className="item name">
          <div className="label">Company name</div>
          <div className="value">
            <span
              id={state.nameTooltipId}
              className="company-name-link"
              onClick={onOpenEntryDetails}
            >
              {entry.name || PUNCTUATION.EMPTY_VALUE}
            </span>
            <Tooltip
              anchorSelect={`#${state.nameTooltipId}`}
              multiline={true}
              variant="dark"
              place={'bottom-center'}
              opacity={1}
              content={'View details'}
              className={'company-name-view-details-tooltip'}
            />
            <RegisterStatusBadge company={entry} />
          </div>
        </div>
        <div className="item headquarter">
          <div className="label">Location</div>
          <div className="value">{entry.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
        </div>
        <div className="item industry">
          <div className="label">Industry</div>
          <div className="value">
            {/* {entry.industries && entry.industries.length
              ? utilsStore.shortenList(entry.industries, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList
              list={entry.industries}
              alignTopMeasurementsEnabled
              alignTopMeasurementsRowQuerySelector=".page-team-feedback > .feedback-details > .lists > .lists > .table-responsive > .row"
              alignTopMeasurementsItemsContainersQuerySelector="> .item"
            />
          </div>
        </div>
        <div className="item value-chain">
          <div className="label">Value chain</div>
          <div className="value">
            {/* {entry.valueChain && entry.valueChain.length
              ? utilsStore.shortenList(entry.valueChain, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList list={entry.valueChain} />
          </div>
        </div>
        <div className="item products">
          <div className="label">Products</div>
          <div className="value">
            {/* {entry.products && entry.products.length
              ? utilsStore.shortenList(entry.products, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList list={entry.products} />
          </div>
        </div>
        <div className="item revenue">
          <div className="label">Sales (€M)</div>
          <div className="value">
            {entry.revenue ? (
              <>
                {!!entry.isRevenueGrossProfit && (
                  <>
                    <div id={state.grossProfitTooltipId} className="default-black-info-circle-wrap">
                      <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                    </div>
                    <Tooltip
                      anchorSelect={`#${state.grossProfitTooltipId}`}
                      multiline={true}
                      variant="dark"
                      place="bottom-center"
                      opacity={1}
                      content={
                        <>
                          This company was included
                          <br />
                          based on reported gross profit,
                          <br />
                          not revenue.
                        </>
                      }
                      className={'default-black-info-tooltip-classname'}
                    />
                  </>
                )}
                <font
                  id={state.basicRevenueTooltipId}
                  style={entry.isRevenueBasic ? { color: '#348cf9' } : {}}
                >
                  {entry.revenue}
                </font>
                <span className="year" style={entry.isRevenueBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {entry.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
                {!!entry.isRevenueBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicRevenueTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
        <div className="item employees">
          <div className="label">Employees</div>
          <div className="value">
            {!isNaN(entry.employees) && entry.employees !== null ? (
              <>
                <font
                  id={state.basicEmployeesTooltipId}
                  style={entry.isEmployeesBasic ? { color: '#348cf9' } : {}}
                >
                  {formatNumber((+entry.employees).toFixed(2))}
                </font>
                <span className="year" style={entry.isEmployeesBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {entry.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>

                {!!entry.isEmployeesBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicEmployeesTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>

        {state.layout === 'desktop' && (
          <div className="item feedback">
            <IconButton
              type={ICON_BUTTON_TYPES.GREEN}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'up')}
              filled={isFeedbackMarked(entry.id, 'up')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.YELLOW}
              icon={ICON_BUTTON_ICONS.QUESTION_LG}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'question')}
              filled={isFeedbackMarked(entry.id, 'question')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'down')}
              filled={isFeedbackMarked(entry.id, 'down')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
          </div>
        )}
        <div className="item actions profiler-search-actions ">
          {state.layout === 'desktop' && (
            <>
              <IconButton
                wrapClassName="view-details-btn-desktop"
                tooltipText="View details"
                onClick={onOpenEntryDetails}
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="watchlist-action-btn-desktop"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.WATCHLIST}
                disabled={
                  !!profilerProjectStore.companiesInWatchlistAction[entry.id] ||
                  !profilerAccessStore.canViewProfilerWatchlist
                }
                tooltipContent={watchlistButtonTooltipContent}
                tooltipPosition={watchlistButtonTooltipPosition}
                tooltipClickable={watchlistButtonTooltipClickable}
                filled={isInWatchlist}
                onClick={onWatchlistButtonClick}
              />
            </>
          )}
          {state.layout === 'mobile' && (
            <>
              <IconButton
                wrapClassName="view-details-btn-mobile"
                innerText="View details"
                onClick={onOpenEntryDetails}
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="watchlist-action-btn-mobile"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.WATCHLIST}
                disabled={
                  profilerProjectStore.isLoadingWatchlist ||
                  profilerProjectStore.initialLoadWatchlist ||
                  !!profilerProjectStore.companiesInWatchlistAction[entry.id] ||
                  !profilerAccessStore.canViewProfilerWatchlist
                }
                tooltipContent={watchlistButtonTooltipContent}
                tooltipPosition={watchlistButtonTooltipPosition}
                tooltipClickable={watchlistButtonTooltipClickable}
                filled={isInWatchlist}
                onClick={onWatchlistButtonClick}
              />
            </>
          )}
        </div>
      </div>
    );
  }
);

export default ProfilerFeedbackSearchResultsTabEntry;
